import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ShieldPlus, X } from "lucide-react";

import { useUser } from "@/hooks/useUser";
import { ModalShell } from "@/components/ui/modal-shell";
import { useAppTranslation } from "@/i18n/useAppTranslation";

interface MfaStatus {
  showHostNudge: boolean;
  snoozeDays: number;
}

/**
 * Task #3063 — Host MFA nudge.
 *
 * Mounted globally inside AuthModalProvider. On every authenticated
 * page load we ask /auth/mfa/status whether this host is a) eligible
 * for the nudge (received-payout flag) and b) outside the
 * `mfa_nudge_snoozed_until` cooldown. If both are true we render a
 * one-shot dialog with three outs:
 *
 *   - "Set up now" — navigates to /settings#security
 *   - "Remind me later" — POST /auth/mfa/nudge/snooze; cooldown window
 *     defined by the policy module (HOST_NUDGE_SNOOZE_DAYS, 14d).
 *   - "Don't ask again" — POST /auth/mfa/nudge/dismiss; the host is
 *     never auto-prompted again and must enrol from settings on their
 *     own. We deliberately keep this exit visible so we don't bully
 *     hosts who have weighed the trade-off.
 */
export function HostMfaNudge() {
  const { t } = useAppTranslation();
  const { session, user, profile } = useUser();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<MfaStatus | null>(null);

  useEffect(() => {
    if (!session?.access_token || !user || !profile) return;
    const apiBase = import.meta.env.BASE_URL || "/";
    fetch(`${apiBase}api/auth/mfa/status`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: MfaStatus | null) => {
        if (!j) return;
        setStatus(j);
        if (j.showHostNudge) setOpen(true);
      })
      .catch(() => { /* nudge is best-effort */ });
  }, [session?.access_token, user?.id, profile?.id]);

  async function snooze() {
    const apiBase = import.meta.env.BASE_URL || "/";
    await fetch(`${apiBase}api/auth/mfa/nudge/snooze`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
    }).catch(() => { /* noop */ });
    setOpen(false);
  }

  async function dismiss() {
    const apiBase = import.meta.env.BASE_URL || "/";
    await fetch(`${apiBase}api/auth/mfa/nudge/dismiss`, {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
    }).catch(() => { /* noop */ });
    setOpen(false);
  }

  if (!open || !status) return null;

  return (
    <ModalShell open={open} onClose={() => void snooze()} size="md">
      <div className="space-y-4 p-2">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2 text-primary">
            <ShieldPlus className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{t("auth.host_nudge_title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("auth.host_nudge_body")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void snooze()}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-2 pt-2">
          <Link
            href="/settings#security"
            onClick={() => setOpen(false)}
            className="primary-action inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold"
          >
            {t("auth.setup_two_factor")}
          </Link>
          <button
            type="button"
            onClick={() => void snooze()}
            className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            {t("auth.host_nudge_remind", { days: status.snoozeDays })}
          </button>
          <button
            type="button"
            onClick={() => void dismiss()}
            className="text-center text-xs text-muted-foreground hover:underline"
          >
            {t("auth.host_nudge_dismiss")}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
